import React, { useState, useEffect } from 'react';
import { X, Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import chatService from '../../services/chatService';

/**
 * ChangeFocusImageModal - Upload or change cover image for a Focus
 * 
 * Props:
 * - isOpen: boolean
 * - onClose: function
 * - focus: object - the focus to change image for
 * - onImageChanged: function - callback after successful image change
 */
export default function ChangeFocusImageModal({ isOpen, onClose, focus, onImageChanged }) {
  const { user } = useAuth();
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && focus) {
      // Show current image if exists
      setImagePreview(focus.cover_image_url || null);
      setImageFile(null);
      setError(null);
    }
  }, [isOpen, focus]);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }
    
    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    setError(null);
  };

  const handleRemoveImage = async () => {
    if (!focus) return;
    
    setIsUploading(true);
    setError(null);
    
    try {
      // Update focus with null image
      const { error: updateError } = await chatService.updateFocus(user.id, focus.id, {
        cover_image_url: null
      });
      
      if (updateError) {
        throw new Error(updateError);
      }
      
      // Callback to refresh focus list
      if (onImageChanged) {
        onImageChanged();
      }
      
      onClose();
    } catch (err) {
      console.error('Error removing image:', err);
      setError(err.message || 'Failed to remove image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (!imageFile || !focus) return;
    
    setIsUploading(true);
    setError(null);
    
    try {
      // Upload to Supabase storage
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `focus-${focus.id}-${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('user_uploads')
        .upload(fileName, imageFile, {
          cacheControl: '3600',
          upsert: true
        });
      
      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('user_uploads')
        .getPublicUrl(fileName);
      
      // Update focus with new image URL
      const { error: updateError } = await chatService.updateFocus(user.id, focus.id, {
        cover_image_url: publicUrl
      });
      
      if (updateError) {
        throw new Error(updateError);
      }
      
      // Callback to refresh focus list
      if (onImageChanged) {
        onImageChanged();
      }
      
      onClose();
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen || !focus) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
              <ImageIcon size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Change Cover Image
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {focus.title}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          
          {/* Upload Area */}
          {!imagePreview ? (
            <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload size={48} className="text-gray-400 mb-4" />
                <p className="mb-2 text-sm text-gray-600">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
              </div>
              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleImageSelect}
              />
            </label>
          ) : (
            <div className="relative rounded-xl overflow-hidden border border-gray-200">
              <img 
                src={imagePreview} 
                alt="Cover preview" 
                className="w-full h-64 object-cover"
              />
              <div className="absolute top-3 right-3 flex gap-2">
                {/* Change image button */}
                <label className="p-2 bg-white hover:bg-gray-50 rounded-lg transition-colors cursor-pointer shadow-lg flex items-center gap-1.5">
                  <Upload size={14} className="text-gray-700" />
                  <span className="text-xs font-medium text-gray-700">Change</span>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageSelect}
                  />
                </label>
                
                {/* Remove image button */}
                {focus.cover_image_url && (
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    disabled={isUploading}
                    className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shadow-lg disabled:opacity-50"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-5 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={isUploading}
            className="px-5 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isUploading || !imageFile}
            className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isUploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={16} />
                Save Image
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

